import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";

import Regions from "../Regions";
import classes from "../../StateProvinceSelectionSlide.module.scss";
import { GeoState, GeoTargetOptionsData } from "../../../../../types/campaignTypes";

type SetStateAction<S> = S | ((prevState: S) => S);

type CheckBoxes = {
  CAN: Record<number, boolean>;
  USA: Record<number, boolean>;
};

type StateProvinceSelectionDasboardProps = {
  geoTargetOptions: GeoTargetOptionsData;
  selectedGeoTargetOptions: string[];
  setSelectedGeoTargetOptions: (selectedGeoTargetOptions: SetStateAction<string[]>) => void;
};

const transformGeoTargetOptionsToSelectOptionsState = (selectedGeoTargetOptions: string[], states: GeoState[]) => {
  const result = states.reduce((accum, state) => {
    return {
      ...accum,
      [state.id]: selectedGeoTargetOptions.includes(String(state.id)),
    };
  }, {} as Record<number, boolean>);

  const allSelected = states.length > 0 && states.every((state) => selectedGeoTargetOptions.includes(String(state.id)));
  result[-1] = allSelected;

  return result;
};

const StateProvinceSelectionDasboard: FC<StateProvinceSelectionDasboardProps> = ({
  geoTargetOptions,
  selectedGeoTargetOptions,
  setSelectedGeoTargetOptions,
}) => {
  const [checkBoxes, setCheckBoxes] = useState<CheckBoxes>(() => {
    return {
      CAN: transformGeoTargetOptionsToSelectOptionsState(selectedGeoTargetOptions, geoTargetOptions.Canada.states),
      USA: transformGeoTargetOptionsToSelectOptionsState(
        selectedGeoTargetOptions,
        geoTargetOptions["United States"].states,
      ),
    };
  });

  const usRegions = useMemo(() => geoTargetOptions["United States"].states, [geoTargetOptions]);

  const canadianRegions = useMemo(() => geoTargetOptions.Canada.states, [geoTargetOptions]);

  useEffect(() => {
    setCheckBoxes({
      CAN: transformGeoTargetOptionsToSelectOptionsState(selectedGeoTargetOptions, canadianRegions),
      USA: transformGeoTargetOptionsToSelectOptionsState(selectedGeoTargetOptions, usRegions),
    });
  }, [selectedGeoTargetOptions, canadianRegions, usRegions]);

  const handleSelectAll = (country_code_3: "USA" | "CAN") => (event: ChangeEvent<{}>) => {
    const states = country_code_3 === "USA" ? usRegions : canadianRegions;
    const allChecked = checkBoxes[country_code_3][-1] || false;

    const newCheckBoxValues: Record<number, boolean> = {};
    states.forEach((state) => {
      newCheckBoxValues[state.id] = !allChecked;
    });
    newCheckBoxValues[-1] = !allChecked;

    const newCheckBoxes = {
      ...checkBoxes,
      [country_code_3]: newCheckBoxValues,
    };

    setCheckBoxes(newCheckBoxes);

    const allSelectedOptions: string[] = [];
    Object.entries(newCheckBoxes.USA).forEach(([key, value]) => {
      if (value && Number(key) !== -1) {
        allSelectedOptions.push(key);
      }
    });
    Object.entries(newCheckBoxes.CAN).forEach(([key, value]) => {
      if (value && Number(key) !== -1) {
        allSelectedOptions.push(key);
      }
    });

    setSelectedGeoTargetOptions(allSelectedOptions);
  };

  const handleChange = (event: ChangeEvent<{}>) => {
    const { name } = event.target as HTMLInputElement;
    const stateId = Number(name);

    const country_code_3 = stateId === -1 || usRegions.some((s) => s.id === stateId) ? "USA" : "CAN";
    const checked = checkBoxes[country_code_3][stateId];
    const states = country_code_3 === "USA" ? usRegions : canadianRegions;

    const newCheckBoxValues = {
      ...checkBoxes[country_code_3],
      [stateId]: !checked,
    };

    if (stateId !== -1) {
      const allStatesSelected = states.every((state) => {
        if (state.id === stateId) {
          return !checked;
        }
        return checkBoxes[country_code_3][state.id] || false;
      });
      newCheckBoxValues[-1] = allStatesSelected;
    }

    const newCheckBoxes = {
      ...checkBoxes,
      [country_code_3]: newCheckBoxValues,
    };

    setCheckBoxes(newCheckBoxes);

    const allSelectedOptions: string[] = [];
    Object.entries(newCheckBoxes.USA).forEach(([key, value]) => {
      if (value && Number(key) !== -1) {
        allSelectedOptions.push(key);
      }
    });
    Object.entries(newCheckBoxes.CAN).forEach(([key, value]) => {
      if (value && Number(key) !== -1) {
        allSelectedOptions.push(key);
      }
    });

    setSelectedGeoTargetOptions(allSelectedOptions);
  };

  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.regionsContainer}>
        <div className={classes.regionsColumn}>
          <div className={classes.regionsColumnHeader}>
            <Typography variant="h2" className={classes.regionsColumnHeading}>
              United States Regions
            </Typography>
          </div>
          <Regions
            regions={usRegions}
            checkBoxes={checkBoxes.USA}
            label="Select regions from the US"
            country_code_3="USA"
            handleSelectAll={handleSelectAll("USA")}
            handleChange={handleChange}
          />
        </div>

        <div className={classes.regionsColumn}>
          <div className={classes.regionsColumnHeader}>
            <Typography variant="h2" className={classes.regionsColumnHeading}>
              Canadian Regions
            </Typography>
          </div>
          <Regions
            regions={canadianRegions}
            checkBoxes={checkBoxes.CAN}
            label="Select regions from Canada"
            country_code_3="CAN"
            handleSelectAll={handleSelectAll("CAN")}
            handleChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(StateProvinceSelectionDasboard);
